<script>
import SearchLayout from "@/Layouts/SearchLayout.vue";
import TD from "@/Components/Table/Element.vue";
import Label from "@/Components/Settings/Label.vue";
import Input from "@/Components/Settings/Input.vue";
import Button from "@/Components/Button.vue";

import PatientSearch from "@/Components/Calendar/PatientSearch.vue";
import { ref, onMounted } from "vue";
import { Inertia } from "@inertiajs/inertia";

export default { layout: SearchLayout };
</script>


<script setup>
const searchQ = ref("");
const obsElement = ref(null);
const allAppointments = ref(props.appointments.data || []);
const firstname = ref(props.appointments?.meta?.patient_firstname);
const lastname = ref(props.appointments?.meta?.patient_lastname);
const patient_number = ref(props.appointments?.meta?.patient_number);
const props = defineProps({
  errors: Object,
  appointments: Object,
});

const canEdit = Inertia.page.props.auth.can["edit appointments"];

onMounted(() => {
  const observer = new IntersectionObserver((entries) =>
    entries.forEach((entry) => entry.isIntersecting && loadMoreAppointments(), {
      rootMargin: "-150px 0px 0px 0px",
    })
  );

  observer.observe(obsElement.value);
});

function editAppointment(appointment) {
  if (canEdit) {
    Inertia.visit(`/termin-bearbeiten/${appointment.id}`, {
      method: "get",
    });
  }
}

function loadMoreAppointments() {
  if (
    props.appointments.length === 0 ||
    props.appointments.links.next === null
  ) {
    return;
  }

  Inertia.get(
    props.appointments.links.next,
    {},
    {
      preserveState: true,
      preserveScroll: true,
      only: ["appointments"],
      onSuccess: () => {
        allAppointments.value = [
          ...allAppointments.value,
          ...props.appointments.data,
        ];
        window.history.replaceState(
          {},
          "Termin finden",
          "/termin-finden?patient_number=" + patient_number.value
        );
      },
    }
  );
}

function selectPatient(patient) {
  firstname.value = patient.firstname;
  lastname.value = patient.lastname;
  patient_number.value = patient.patient_number;
}

function searchAppointments() {
  Inertia.get(
    "/termin-finden",
    {
      patient_number: patient_number.value,
    },
    {
      preserveState: true,
      replace: true,
      onSuccess: () => {
        allAppointments.value = [
          ...allAppointments.value,
          ...props.appointments.data,
        ];
      },
    }
  );
}
</script>

<template>
  <div class="w-[33%] pr-16">
    <div class="">
      <Label value="Patient/in suchen" />

      <PatientSearch @selectPatient="selectPatient" />
    </div>

    <div class="mt-8">
      <Button @click="searchAppointments" class="w-full"
        >Patient/in suchen</Button
      >
    </div>

    <div class="mt-8">
      <Label for="patient_number" value="Patientennummer" />
      <Input
        id="patient_number"
        type="text"
        class="mt-1 block w-full"
        v-model="patient_number"
        required
        placeholder=""
        disabled
      />
      <div class="mt-2 text-red-600 text-sm" v-if="errors.patient_number">
        {{ errors.patient_number }}
      </div>
    </div>

    <div class="mt-8">
      <Label for="firstname" value="Vorname" />
      <Input
        id="firstname"
        type="text"
        class="mt-1 block w-full"
        v-model="firstname"
        required
        placeholder=""
        disabled
      />
      <div class="mt-2 text-red-600 text-sm" v-if="errors.firstname">
        {{ errors.firstname }}
      </div>
    </div>

    <div class="mt-8">
      <Label for="lastname" value="Nachname" />
      <Input
        id="lastname"
        type="text"
        class="mt-1 block w-full"
        v-model="lastname"
        required
        placeholder=""
        disabled
      />
      <div class="mt-2 text-red-600 text-sm" v-if="errors.lastname">
        {{ errors.lastname }}
      </div>
    </div>
  </div>

  <div class="w-[67%]">
    <div v-if="appointments?.meta" class="mb-8 text-lg font-light">
      <span class="font-semibold">{{ appointments.meta.total }} Termine</span>
      für {{ lastname }},
      {{ firstname }}
    </div>
    <div class="max-h-[530px] overflow-y-scroll">
      <table class="min-w-full">
        <thead class="bg-white border-b">
          <tr class="sticky -top-px border-white border-t bg-white">
            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Behandler/in
            </th>
            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Behandlungsraum
            </th>
            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Behandlungstyp
            </th>
            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Datum
            </th>
            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Wochentag
            </th>
            <th
              scope="col"
              class="
                text-sm
                font-medium
                text-gray-900
                px-6
                py-4
                text-left
                border-x-2
              "
            >
              Zeit
            </th>
          </tr>
        </thead>
        <tbody class="">
          <tr
            v-for="(appointment, index) in allAppointments"
            :key="appointment.id"
            class="border-b"
            @click="editAppointment(appointment)"
            :class="{
              'bg-gray-100': index % 2 === 0,
              'cursor-pointer': canEdit,
            }"
          >
            <TD>
              {{ `${appointment.practitioner}` }}
            </TD>
            <TD> {{ `${appointment.room}` }} </TD>
            <TD>
              <div
                class="w-3 h-3 rounded-full mr-2 inline-block"
                :style="`background-color:${appointment.type.color}`"
              ></div>
              {{ `${appointment.type.name}` }}
            </TD>
            <TD> {{ `${appointment.date}` }} </TD>
            <TD> {{ `${appointment.weekday}` }} </TD>
            <TD> {{ `${appointment.time} Uhr` }} </TD>
          </tr>

          <!-- <tr>
          <TD></TD>
          <TD></TD>
          <TD></TD>
          <TD></TD>
          <TD></TD>
          <TD></TD>
        </tr> -->
        </tbody>
      </table>
      <div
        class="sticky bottom-0 h-10 w-full bg-gradient-to-t from-white"
      ></div>
      <div class="h-8 w-full bg-transparent" ref="obsElement"></div>
    </div>
  </div>
</template>